import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Country, GlobalMessageService, GlobalMessageType, PageMetaService, Region, User } from '@spartacus/core';
import { UpdateProfileComponent, UpdateProfileComponentService } from '@spartacus/user/profile/components';
import { UserProfileFacade } from '@spartacus/user/profile/root';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { ICON_TYPE } from '../../../../../custom/icons/icon.model';
import { CustomUserAddressService } from '../../../../custom/core/user/facade/profile/custom-user-address.service';
import { CustomUserSignUp } from '../../../../custom/model/user-profile.model';
import { CustomValidators } from '../../../../custom/shared/utils/validators/custom-validators';
import { JobType, UserTaxGroup } from '../../../../spartacus-custom.module';
import { CustomUserRegisterFacade } from '../root/facade/custom-user-register.facade';
@Component({
  selector: 'app-custom-update-profile',
  templateUrl: './custom-update-profile.component.html',
  styleUrls: ['./custom-update-profile.component.scss'],
  standalone: false
})
export class CustomUpdateProfileComponent extends UpdateProfileComponent implements OnInit, OnDestroy {
  protected busy$ = new BehaviorSubject(false);
  protected subscriptions: Subscription = new Subscription();
  countries$?: Observable<Country[]>;
  pageTitle: string = '';
  pageDescription: string = '';
  user?: User;
  form: UntypedFormGroup;
  checked = true;
  professional = false;
  iconTypes = ICON_TYPE;
  jobTypes$?: Observable<JobType[]>;
  selectedJobType: string = '';
  userTaxGroups$?: Observable<UserTaxGroup[]>;
  selectedUserTaxGroup: string = '';
  currentUser?: User;

  constructor(
    protected service: UpdateProfileComponentService,
    protected fb: UntypedFormBuilder,
    protected userAddressService: CustomUserAddressService,
    protected customuserRegister: CustomUserRegisterFacade,
    protected userProfile: UserProfileFacade,
    protected globalMessageService: GlobalMessageService,
    protected pageMetaService: PageMetaService
  ) {
    super(service);
    this.form = this.fb.group({
      titleCode: [''],
      firstName: ['', [Validators.required, Validators.maxLength(70)]],
      lastName: [''],
      contactPerson: [''],
      jobType: this.fb.group({
        code: ['', this.professional ? Validators.required : '']
      }),
      exclusiveRole: [!this.professional ? 'ohgarcustomergroup' : 'ohgarprofessionalgroup'],
      documentIdentifier: ['', [CustomValidators.documentIdentifier('ohgarprofessionalgroup', 'ES')]],
      mobilePhone: ['', [Validators.required, CustomValidators.phoneValidator]],
      birthday: [''],
      customerId: [''],
      visibleInAddressBook: [''],
      defaultBillingAddress: this.fb.group({
        line1: ['', [Validators.required, Validators.max(70)]],
        town: ['', [Validators.required, Validators.maxLength(35)]],
        postalCode: ['', [Validators.required, Validators.maxLength(5), Validators.minLength(5), CustomValidators.onlyNumbers]],
        country: this.fb.group({
          isocode: ['ES', Validators.required]
        }),
        region: this.fb.group({
          isocode: ['', [Validators.required, CustomValidators.regionCodeStartsWith('postalCode')]]
        }),
        billingAddress: [true],
        visibleInAddressBook: [true]
      }),
      userTaxGroup: ['', this.professional ? Validators.required : '']
    });
    this.subscriptions.add(
      this.user$.subscribe(
        (user: User) => {
          this.currentUser = user;
          let formattedUser = user;
          if (user?.birthday) {
            formattedUser = { ...user, birthday: user.birthday.split('T')[0] };
          }
          this.form?.patchValue(formattedUser);
        },
        switchMap((_user: User) => this.busy$)
      )
    );

    this.validatePostalCode();
  }

  regions$: Observable<Region[]> = this.userAddressService.getRegions('ES');
  user$: Observable<User> = this.userProfile.get().pipe(filter((user): user is User => Boolean(user)));
  isUpdating$: Observable<boolean> = this.service.isUpdating$;

  collectDataFromDocumentIdentifierValidatorForm(formData: any): CustomUserSignUp {
    const { firstName, documentIdentifier, exclusiveRole } = formData;

    return {
      firstName,
      documentIdentifier,
      exclusiveRole
    };
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.pageMetaService.getMeta().subscribe((data) => {
        this.pageTitle = data.title;
        this.pageDescription = data.description;
      })
    );
    this.countries$ = this.userAddressService.getDeliveryCountries().pipe(
      tap((countries: Country[]) => {
        if (Object.keys(countries).length === 0) {
          this.userAddressService.loadDeliveryCountries();
        }
      })
    );
    this.jobTypes$ = this.customuserRegister.getJobTypes().pipe(
      tap((jobTypes: JobType[]) => {
        if (Object.keys(jobTypes).length === 0) {
          this.customuserRegister.loadJobTypes();
        }
      })
    );

    this.subscriptions.add(this.user$.subscribe((user) => (this.selectedJobType = user?.jobType?.code)));

    this.userTaxGroups$ = this.customuserRegister.getUserTaxGroups().pipe(
      tap((userTaxGroups: UserTaxGroup[]) => {
        if (Object.keys(userTaxGroups).length === 0) {
          this.customuserRegister.loadUserTaxGroups();
        }
      })
    );
  }

  updateProfile(): void {
    this.busy$.next(true);
    let formattedDate = '';
    const birthdayValue = this.form.get('birthday').value;
    if (birthdayValue) {
      formattedDate = new Date(birthdayValue).toISOString();
    }
    const formData = { ...this.form.value, birthday: formattedDate };
    this.subscriptions.add(
      this.userProfile.update(formData).subscribe({
        next: () => this.onSuccess(),
        error: (error: Error) => this.onError(error)
      })
    );
  }

  updateDatepicker(filterKey: string): void {
    this.form.get(filterKey)?.updateValueAndValidity();
  }

  validatePostalCode(): void {
    this.form.get('defaultBillingAddress.region.isocode')?.updateValueAndValidity();
  }

  jobTypeSelected(jobType: JobType): void {
    this.form.controls.jobType.setValue(jobType.code);
  }

  isProfessional(): boolean {
    this.subscriptions.add(this.user$.subscribe((user) => (this.professional = user?.professional)));
    return this.professional;
  }

  isValidateTaxIdentification(): void {
    if (this.form.valid) {
      this.documentIdentifierValidator();
    } else {
      this.form.markAllAsTouched();
    }
  }

  documentIdentifierValidator(): void {
    this.subscriptions.add(
      this.customuserRegister.documentIdentifierValidator(this.collectDataFromDocumentIdentifierValidatorForm(this.form.value)).subscribe({
        next: () => {
          this.updateProfile();
        }
      })
    );
  }

  protected onSuccess(): void {
    this.globalMessageService.add(
      {
        key: 'updateProfileForm.profileUpdateSuccess'
      },
      GlobalMessageType.MSG_TYPE_CONFIRMATION
    );

    this.busy$.next(false);
  }

  protected onError(error: Error): void {
    this.globalMessageService.add(
      {
        key: 'updateProfileForm.profileUpdateError',
        params: { errorList: error }
      },
      GlobalMessageType.MSG_TYPE_ERROR
    );
    this.busy$.next(false);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
